import * as d3 from "d3";
import { resolveDataField } from "./colorScale";

/** Tukey (1.5×IQR) whiskers with outliers, or full sorted data range. */
export type WhiskerMode = "tukey" | "full";

export interface StripPoint {
  value: number;
  /** Numeric TWS for sort order only; use `NaN` or omit when unknown (sorted last). */
  tws: number;
}

export interface TukeyBoxStats {
  q1: number;
  q2: number;
  q3: number;
  mean: number;
  whiskerMin: number;
  whiskerMax: number;
  outliers: number[];
  iqr: number;
  count: number;
}

export const TUKEY_MULT = 1.5;

/** Horizontal fraction in [0, 1] for strip point `j` of `n` (even spacing with end margin). */
export function stripHorizontalFraction(j: number, n: number): number {
  if (n <= 0) return 0.5;
  return (j + 1) / (n + 1);
}

/** Parse bin labels like "8-10", "8.0–10.0" to a midpoint for ordering. */
export function twsBinMidpointFromString(raw: string): number | null {
  const s = String(raw).trim();
  if (!s) return null;
  const normalized = s.replace(/–|—/g, "-");
  const parts = normalized.split("-").map((p) => parseFloat(p.trim())).filter((x) => Number.isFinite(x));
  if (parts.length >= 2) return (parts[0]! + parts[1]!) / 2;
  if (parts.length === 1) return parts[0]!;
  return null;
}

function readFiniteNumber(v: unknown): number | null {
  if (v === undefined || v === null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Numeric TWS from a row for strip ordering (not display).
 * Uses preferred field name when provided, then resolveDataField fallbacks.
 */
export function getNumericTwsFromRow(
  row: Record<string, unknown>,
  options?: { preferredTwsField?: string | null }
): number | null {
  const tryField = (field: string | null | undefined): number | null => {
    if (!field) return null;
    const v = resolveDataField(row as any, field);
    return readFiniteNumber(v);
  };

  const fromPreferred = tryField(options?.preferredTwsField ?? undefined);
  if (fromPreferred !== null) return fromPreferred;

  const fallbackFields = [
    "tws_avg",
    "Tws_kt_avg",
    "Tws_kts_avg",
    "tws_kts_avg",
    "Tws_kts",
    "Tws_kph",
    "tws_kts",
    "tws_kph",
    "Tws",
    "tws",
  ];

  for (const f of fallbackFields) {
    const n = tryField(f);
    if (n !== null) return n;
  }

  const binRaw = row.tws_bin ?? row.Tws_bin;
  if (binRaw !== undefined && binRaw !== null) {
    if (typeof binRaw === "number" && Number.isFinite(binRaw)) return binRaw;
    const mid = twsBinMidpointFromString(String(binRaw));
    if (mid !== null) return mid;
  }

  return null;
}

/** Finite-TWS points first (ascending TWS, stable by input order); missing TWS after in original input order. */
export function sortStripPointsByTws(points: StripPoint[]): StripPoint[] {
  const finite: Array<{ point: StripPoint; idx: number }> = [];
  const missing: Array<{ point: StripPoint; idx: number }> = [];
  points.forEach((p, idx) => {
    if (Number.isFinite(p.tws)) finite.push({ point: p, idx });
    else missing.push({ point: { ...p, tws: NaN }, idx });
  });
  // IMPORTANT: never tie-break by y-value here, otherwise strips look value-sorted.
  finite.sort((a, b) => a.point.tws - b.point.tws || a.idx - b.idx);
  return [
    ...finite.map((x) => x.point),
    ...missing.map((x) => x.point),
  ];
}

export function computeTukeyBoxStats(
  valsSortedAsc: readonly number[],
  whiskerMode: WhiskerMode,
  tukeyMult: number = TUKEY_MULT
): TukeyBoxStats {
  const vals = valsSortedAsc;
  const n = vals.length;
  const q1 = d3.quantile(vals as number[], 0.25) ?? 0;
  const q2 = d3.quantile(vals as number[], 0.5) ?? 0;
  const q3 = d3.quantile(vals as number[], 0.75) ?? 0;
  const mean = n ? (d3.mean(vals as number[]) ?? 0) : 0;
  const iqr = q3 - q1;
  let whiskerMin: number;
  let whiskerMax: number;
  let outliers: number[];
  if (whiskerMode === "tukey" && n > 0) {
    const lowerFence = q1 - tukeyMult * iqr;
    const upperFence = q3 + tukeyMult * iqr;
    const inRange = (vals as number[]).filter((v) => v >= lowerFence && v <= upperFence);
    whiskerMin = inRange.length ? (d3.min(inRange) ?? vals[0]!) : q1;
    whiskerMax = inRange.length ? (d3.max(inRange) ?? vals[n - 1]!) : q3;
    outliers = (vals as number[]).filter((v) => v < lowerFence || v > upperFence);
  } else {
    whiskerMin = n ? vals[0]! : 0;
    whiskerMax = n ? vals[n - 1]! : 0;
    outliers = [];
  }
  return { q1, q2, q3, mean, whiskerMin, whiskerMax, outliers, iqr, count: n };
}

export type ChartSailFilterMode = "upwind" | "downwind" | "all";

export interface FilterRowsByChartSailFiltersOptions {
  /** When `all`, returns rows unchanged. */
  mode: ChartSailFilterMode;
  /** Instantaneous TWA field (e.g. from defaultChannelsStore.twaName()). */
  twaField?: string;
}

/**
 * Same semantics as performance scatter: prefer `Twa_deg_avg` / `twa_deg_avg` for stable upwind/downwind,
 * else fall back to `twaField` on the row.
 */
export function filterRowsByChartSailFilters<T extends Record<string, unknown>>(
  rows: T[],
  options: FilterRowsByChartSailFiltersOptions
): T[] {
  if (!rows.length || options.mode === "all") return rows;

  const twaField = options.twaField;

  return rows.filter((d) => {
    const twaAvg = d.Twa_deg_avg ?? d.twa_deg_avg;
    if (twaAvg !== undefined && twaAvg !== null && !isNaN(Number(twaAvg))) {
      const t = Number(twaAvg);
      if (options.mode === "upwind") return Math.abs(t) < 90;
      if (options.mode === "downwind") return Math.abs(t) >= 90;
      return true;
    }

    if (twaField) {
      const raw = (d as any)[twaField] ?? (d as any).Twa_deg ?? (d as any).twa_deg ?? (d as any).Twa ?? (d as any).twa;
      if (raw !== undefined && raw !== null && !isNaN(Number(raw))) {
        const t = Number(raw);
        if (options.mode === "upwind") return Math.abs(t) < 90;
        if (options.mode === "downwind") return Math.abs(t) >= 90;
      }
    }

    return false;
  });
}
